import { create } from 'zustand';

/**
 * Status filter values for the MDM devices page.
 * - `all` — no client-side filter applied.
 * - `online` — device heartbeat is fresh.
 * - `offline` — heartbeat missing or stale.
 * - `stale` — device has not checked in for an extended window (but not fully offline).
 */
export type MdmDeviceStatusFilter = 'all' | 'online' | 'offline' | 'stale';

/**
 * Fleet filter:
 * - `undefined` — "All fleets" (no fleet filter).
 * - `null` — "Default" (devices with fleetId=null).
 * - `string` — specific fleetId.
 */
export type MdmDeviceFleetFilter = string | null | undefined;

interface MdmDevicesFilterState {
  searchText: string;
  fleetId: MdmDeviceFleetFilter;
  status: MdmDeviceStatusFilter;
  os: string[];
  tags: string[];

  setSearchText: (text: string) => void;
  setFleetId: (fleetId: MdmDeviceFleetFilter) => void;
  setStatus: (status: MdmDeviceStatusFilter) => void;
  setOs: (os: string[]) => void;
  setTags: (tags: string[]) => void;
  clear: () => void;
  hasActive: () => boolean;
}

export const useMdmDevicesFilterStore = create<MdmDevicesFilterState>((set, get) => ({
  searchText: '',
  fleetId: undefined,
  status: 'all',
  os: [],
  tags: [],

  setSearchText: (searchText) => set({ searchText }),
  setFleetId: (fleetId) => set({ fleetId }),
  setStatus: (status) => set({ status }),
  setOs: (os) => set({ os }),
  setTags: (tags) => set({ tags }),
  clear: () => set({ searchText: '', fleetId: undefined, status: 'all', os: [], tags: [] }),
  hasActive: () => {
    const s = get();
    return (
      s.searchText.length > 0 ||
      s.fleetId !== undefined ||
      s.status !== 'all' ||
      s.os.length > 0 ||
      s.tags.length > 0
    );
  },
}));
